import React from 'react';
import { useChartOverlays } from '../context/ChartOverlayContext';
import PropTypes from 'prop-types';
import { Box, Tooltip, IconButton, Popover, CircularProgress } from '@mui/material';
import { drawingModeAfterClick, textToolTooltip } from '../utils/textAnnotationSizes';
import { countOverlayGroups, lastOverlaySize } from '../utils/drawingStack';
import { CHART_VIEW, chartViewActionLabel, nextChartView } from '../utils/chartViewMode';
import MinimalMultiTabDialogTrigger from './MinimalMultiTabDialogTrigger';



import ShowChartIcon from '@mui/icons-material/ShowChart';
import BrushIcon from '@mui/icons-material/Brush';
import flaskIcon1 from '../assets/flask-1.png';
import flaskIcon2 from '../assets/flask-2.png';
import flaskIcon3 from '../assets/flask-3.png';
import flaskIcon4 from '../assets/flask-4.png';
import icon5 from '../assets/icon5.png';
import icon6 from '../assets/icon6.png';
import MOAStrategyDialog from './MOAStrategyDialog';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import PublicIcon from '@mui/icons-material/Public';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import FunctionsIcon from '@mui/icons-material/Functions';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import MessageIcon from '@mui/icons-material/Message';
import MessagesPanelDialog from './MessagesPanelDialog';
import BarFloaterSelectionDialog from './BarFloaterSelectionDialog';
import ViewListIcon from '@mui/icons-material/ViewList';
import VedicAspectsStudyDialog from './VedicAspectsStudyDialog';
import { DEFAULT_DRAWING_COLOR } from '../utils/drawingColors';
import './TabPanel-toolbar-gradient.css';
import './TabPanel-tooltip.css';

const displayTooltips = true;

const ICONS = [
  // ... previous icons ...
  { component: '/images/longistudy.png', isImage: true, alt: 'Longitude Study', tooltip: 'Study of well known longitudinal aspects as well as aspects of any angular distance between any two planets', action: 'open_controls' },
  { component: '/images/Declination.png', isImage: true, alt: 'Declination Study', tooltip: 'Study of declination aspects such as parallel and contra-parallel between any two planets', action: 'open_declination_study' },
  { component: <BrushIcon />, tooltip: 'Highlight the area covered by specified degree difference of longitude between any two planets', action: 'open_special_studies' },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      <path d="M4 10 Q8 6, 12 10 T20 10 T28 10" stroke="#1976d2" strokeWidth="2" fill="none" />
      <path d="M4 16 Q8 12, 12 16 T20 16 T28 16" stroke="#388e3c" strokeWidth="2" fill="none" />
      <path d="M4 22 Q8 18, 12 22 T20 22 T28 22" stroke="#fbc02d" strokeWidth="2" fill="none" />
    </svg>
  ), tooltip: 'Draw lines of planetary longitude (with optional mapping to a price) to draw planetary Support and Resistance lines for the chart', action: 'open_planetary_lines_study' },
  { component: <DoubleArrowIcon />, tooltip: 'This is not just based on longitude difference. Vertical lines are drawn only when the selected Planet-A follows the second selected Planet-B', action: 'open_planet_follow_study' },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      {/* Brush 1: Green handle, orange tip */}
      <rect x="8" y="8" width="3" height="14" rx="1" fill="#43a047" />
      <ellipse cx="9.5" cy="8" rx="2.2" ry="2.2" fill="#fb8c00" />
      <rect x="8" y="20" width="3" height="6" rx="1.2" fill="#cfd8dc" />
      {/* Brush 2: Purple handle, pink tip */}
      <rect x="18" y="8" width="3" height="14" rx="1" fill="#8e24aa" />
      <ellipse cx="19.5" cy="8" rx="2.2" ry="2.2" fill="#ec407a" />
      <rect x="18" y="20" width="3" height="6" rx="1.2" fill="#cfd8dc" />
    </svg>
  ), tooltip: 'Highlight the chart area when conditions selected in the study are met. This is an advanced strategy analyzer that decorates the chart as well as prepares summary of statistics of successful long and short trades', action: 'open_area_highlight_study' },
  { component: icon5, isImage: true, alt: 'Planet Characteristics Plot', tooltip: 'Plot longitude difference, declination difference, distance and speed of selected planets over the chart', action: 'open_planet_characteristics_dialog', size: 48, adminOnly: true },
  { component: icon6, isImage: true, alt: 'TrendPulse', tooltip: 'TrendPulse — plot and study planetary aspects, declination, sign/nakshatra ingress, combust, retro and more across selected planets', action: 'open_astrograph_dialog', size: 48, adminOnly: true },
  { component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
  { component: flaskIcon1, isImage: true, alt: 'Strategy 1', tooltip: 'Create and execute a new strategy that is comprised of potentially many criteria of different types. This is an advanced topic and can generate insights that otherwise are not obvious', action: 'open_strategy_design_dialog', size: 48 },
  { component: flaskIcon2, isImage: true, alt: 'Strategy 2', tooltip: 'This Strategy Designer is similar to the previous one but is more focused on providing Vedic Astrology studies that can be combined to build your own strategy', action: 'open_strategy_2_design_dialog', size: 48 },
  { component: flaskIcon3, isImage: true, alt: 'Time and Event Strategy', tooltip: 'This dialog provides numerous studies to find if an event correlates leading to a market move in specified number of days and various combinations thereof', action: 'open_time_and_event_strategy_dialog', size: 48 },
  { component: flaskIcon4, isImage: true, alt: 'MOA Strategy Designer', tooltip: 'Mother Of All Strategy Designer — combine multiple strategy types into one powerful study', action: 'open_moa_strategy_dialog', size: 48 },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      <rect x="4" y="6" width="24" height="20" rx="2.5" fill="#fff" stroke="#1976d2" strokeWidth="1.5" />
      <line x1="4" y1="6" x2="28" y2="26" stroke="#388e3c" strokeWidth="1.2" />
      <line x1="28" y1="6" x2="4" y2="26" stroke="#388e3c" strokeWidth="1.2" />
      <polygon points="16,6 28,16 16,26 4,16" fill="none" stroke="#fbc02d" strokeWidth="1.5" />
    </svg>
  ), tooltip: 'Visual Universe: See chart for any day or animate the chart from 2 hours change to 1 week change every second. This is very useful for advanced studies and generating insights', action: 'open_visual_universe' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Peaks with dots above them, counting back to a 0 anchor on the right. */}
      <polyline points="3,20 8,9 13,17 18,7 24,14" fill="none" stroke="#1b5e20" strokeWidth="1.8" />
      <circle cx="8" cy="5" r="1.9" fill="#1b5e20" /><circle cx="18" cy="3" r="1.9" fill="#1b5e20" />
      <text x="24" y="24" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#1b5e20" fontFamily="sans-serif">0</text>
    </svg>
  ), tooltip: 'Pivot Addition Series (0-Series) — counts back from one of the last three bars and dots the pivots that fall on an addition series (each term the sum of the previous two), within 1 bar. Pivots need 5 higher/lower bars on each side.', action: 'run_pivot_addition_series', adminOnly: true },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <polyline points="3,18 9,8 15,19 21,6 25,13" fill="none" stroke="#0d47a1" strokeWidth="1.8" />
      <circle cx="9" cy="4" r="1.9" fill="#0d47a1" /><circle cx="15" cy="23" r="1.9" fill="#e65100" />
      <text x="23" y="25" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#0d47a1" fontFamily="sans-serif">.618</text>
    </svg>
  ), tooltip: 'Fibonacci Pivot Pairs — scans forward for pivot pairs 20-300 bars apart and dots the pivots landing on Fibonacci fractions and extensions of that span, within 1 bar. Pivots need 5 higher/lower bars on each side. Dashed PROJ lines mark ratios falling beyond the last bar — incomplete setups to watch.', action: 'run_fib_pivot_pairs', adminOnly: true },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Two anchors on the left, dashed projections running right into future space. */}
      <line x1="4" y1="4" x2="4" y2="24" stroke="#455a64" strokeWidth="2" />
      <line x1="10" y1="4" x2="10" y2="24" stroke="#455a64" strokeWidth="2" />
      <line x1="17" y1="5" x2="17" y2="23" stroke="#e65100" strokeWidth="1.8" strokeDasharray="3 2" />
      <line x1="24" y1="5" x2="24" y2="23" stroke="#e65100" strokeWidth="1.8" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Recent Pivot Fib Projections — anchors to the two most recent 5-bar pivots and projects Fibonacci multiples of that interval forward. Dashed orange lines are future targets. Click again to remove.', action: 'toggle_recent_fib_projections', adminOnly: true },
  { component: <AccessTimeIcon />, tooltip: 'Intraday Animation — animate the Vedic chart through time for any location and date to research intraday timeframes', action: 'open_intraday_animation', adminOnly: true },
  { component: <CompareArrowsIcon />, tooltip: 'Compare two time periods of the chart side by side or overlaid to visually identify pattern similarities', action: 'open_chart_comparison' },
  // Spacer after Visual Universe / Intraday icons
  { component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
            // RSI toggle icon (zigzag/sine wave) - 17th from left, 5th from right
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 28 28">
          <polyline points="2,20 7,10 12,18 17,8 22,16 26,12" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>RSI</span>
      </span>
    ), tooltip: 'Toggle RSI overlay in the Volume Panel to combine this technical study with other astro-studies if desired', action: 'toggle_rsi_overlay' },
  // MACD toggle icon (histogram/signal lines)
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 28 28">
          {/* MACD histogram bars */}
          <rect x="4" y="14" width="2" height="6" fill="#1976d2" />
          <rect x="8" y="10" width="2" height="10" fill="#1976d2" />
          <rect x="12" y="16" width="2" height="4" fill="#1976d2" />
          <rect x="16" y="12" width="2" height="8" fill="#1976d2" />
          <rect x="20" y="18" width="2" height="2" fill="#1976d2" />
          {/* MACD signal line */}
          <polyline points="4,20 8,16 12,18 16,14 20,18 24,16" fill="none" stroke="#d32f2f" strokeWidth="2" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>MACD</span>
      </span>
    ), tooltip: 'Toggle standard MACD overlay in the Volume Panel to combine this technical study with other astro-studies if desired', action: 'toggle_macd_overlay' },
  // Insert horizontal lines icon right after MACD
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Correct 3 horizontal parallel lines icon */}
        <svg width="28" height="28" viewBox="0 0 28 28">
          <rect x="4" y="7" width="20" height="3" rx="1.5" fill="#e53935" />
          <rect x="4" y="13" width="20" height="3" rx="1.5" fill="#fbc02d" />
          <rect x="4" y="19" width="20" height="3" rx="1.5" fill="#43a047" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>SR</span>
      </span>
    ), tooltip: 'Draw one or more horizontal lines at desired price levels to view the chart technically with your own support and resistance lines', action: 'open_horizontal_line_dialog' },
    { component: <DonutLargeIcon />, tooltip: 'Configure multiple moving averages and add those to or remove from the chart', action: 'open_pattern_config_dialog' },
  // Drawing tools
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Endpoint dots: these tools now draw a bounded segment, not a full-height line. */}
      <line x1="14" y1="6" x2="14" y2="22" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="14" cy="6" r="2.6" fill="#1976d2" />
      <circle cx="14" cy="22" r="2.6" fill="#1976d2" />
    </svg>
  ), tooltip: 'Draw a vertical segment — click the start point, then click to set how far it extends. Only the second click’s price is used. Press Esc to cancel.', action: 'draw_vertical_line' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* The dashed tail that used to be here implied the line ran off the chart; it no longer does. */}
      <line x1="6" y1="14" x2="22" y2="14" stroke="#e53935" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="6" cy="14" r="2.6" fill="#e53935" />
      <circle cx="22" cy="14" r="2.6" fill="#e53935" />
    </svg>
  ), tooltip: 'Draw a horizontal segment — click the start point, then click to set how far it extends. Only the second click’s date is used. Press Esc to cancel.', action: 'draw_horizontal_line' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="24" x2="24" y2="4" stroke="#388e3c" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ), tooltip: 'Draw a trendline — click two points to connect them. Press Esc to cancel.', action: 'draw_trendline' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <rect x="5" y="7" width="18" height="14" rx="1.5" fill="none" stroke="#ff9800" strokeWidth="2" />
    </svg>
  ), tooltip: 'Draw a rectangle — click two diagonal corners. Press Esc to cancel.', action: 'draw_rectangle' },
  // Rendered by the row below rather than as a static icon: the glyph grows and the badge changes
  // with the size the next click will apply, which static markup cannot show.
  { isTextTool: true, action: 'draw_text' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Spans the full height of the icon, as the tool spans the full height of the pane. */}
      <line x1="14" y1="2" x2="14" y2="26" stroke="#00838f" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ), tooltip: 'Full vertical line — one click draws a line spanning the whole chart height at that bar. Carries no move measurement. Press Esc to cancel.', action: 'draw_vertical_full' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="8" fill="none" stroke="#5e35b1" strokeWidth="2.2" />
    </svg>
  ), tooltip: 'Empty circle — click to place an outlined circle. Click the tool again to cycle Small / Medium / Large. Press Esc to cancel.', action: 'draw_circle_empty' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="8" fill="#5e35b1" />
    </svg>
  ), tooltip: 'Filled circle — click to place a solid circle. Click the tool again to cycle Small / Medium / Large. Press Esc to cancel.', action: 'draw_circle_filled' },

  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Evenly repeating verticals, the anchor solid in the middle. */}
      <line x1="4"  y1="4" x2="4"  y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="11" y1="4" x2="11" y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="18" y1="2" x2="18" y2="26" stroke="#8e24aa" strokeWidth="2.4" />
      <line x1="25" y1="4" x2="25" y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Fixed Length Cycles — enter a cycle length in bars, then click a bar to anchor it. Draws repeating verticals every N bars both backwards through history and forwards into future space.', action: 'open_fixed_cycle_dialog' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* A clock face with dashed verticals marching right: time, projected forward. */}
      <circle cx="8" cy="9" r="5.5" fill="none" stroke="#7b1fa2" strokeWidth="1.8" />
      <line x1="8" y1="9" x2="8" y2="5.5" stroke="#7b1fa2" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="8" y1="9" x2="10.5" y2="10.5" stroke="#7b1fa2" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="16" y1="3" x2="16" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="21" y1="3" x2="21" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="25" y1="3" x2="25" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Fibonacci Time — click two pivots. Projects Fibonacci multiples of the interval between them forward in time from the second point, into future space. Press Esc to cancel.', action: 'draw_fib_time' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="6"  x2="24" y2="6"  stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="12" x2="24" y2="12" stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="18" x2="24" y2="18" stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="22" x2="24" y2="4"  stroke="#01579b" strokeWidth="2" />
    </svg>
  ), tooltip: 'Fibonacci retracement — click the start and end of a move. Draws 23.6/38.2/50/61.8/76.4% levels. Press Esc to cancel.', action: 'draw_fibonacci' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="22" x2="15" y2="11" stroke="#6a1b9a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="15" y1="11" x2="25" y2="1" stroke="#6a1b9a" strokeWidth="2" strokeDasharray="3 2" />
      <circle cx="4" cy="22" r="2.2" fill="#6a1b9a" /><circle cx="15" cy="11" r="2.2" fill="#6a1b9a" />
    </svg>
  ), tooltip: 'Extended trendline — click two points to set the slope, then a third to set how far it projects. Press Esc to cancel.', action: 'draw_extended_trendline' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="3" y1="14" x2="11" y2="14" stroke="#2e7d32" strokeWidth="2" />
      <line x1="11" y1="6"  x2="11" y2="22" stroke="#2e7d32" strokeWidth="2" />
      <line x1="11" y1="14" x2="25" y2="14" stroke="#1b5e20" strokeWidth="2.5" />
      <line x1="11" y1="6"  x2="25" y2="6"  stroke="#2e7d32" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="11" y1="22" x2="25" y2="22" stroke="#2e7d32" strokeWidth="2" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Andrews’ pitchfork — click three pivots. Draws the median from the first through the midpoint of the other two, with parallel prongs. Press Esc to cancel.', action: 'draw_pitchfork' },
  // Colour selector for the five drawing tools above. A marker rather than a component because
  // ICONS is a module-level constant and this control needs state; the row renderer swaps it in,
  // which keeps the toolbar's left-to-right order defined in one place.
  { isDrawingColorPicker: true },
{ component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
      // Chart style toggle icon is injected dynamically as the last icon
];

const TabPanel = (props) => {
  const { clearOverlays, overlays } = useChartOverlays();
  const hasAstroGraph = (overlays || []).some(o => o.type === 'astroGraphLine');
  // Drives the Delete Last button's enabled state and tooltip. Counts undoable UNITS, not
  // overlays: one study run and one Fibonacci retracement are two units, however many lines each
  // put on the chart.
  const drawingCount = countOverlayGroups(overlays);
  const nextUndoSize = lastOverlaySize(overlays);
  // State for VedicAspectsStudyDialog
  const [vedicAspectsOpen, setVedicAspectsOpen] = React.useState(false);
  const { onToggleRSI, onToggleMACD } = props;


  // Add new prop for StrategyDesignDialog
  const { onOpenStrategyDesignDialog, onOpenStrategy2DesignDialog, onOpenPatternConfigDialog, onOpenTimeAndEventStrategyDialog, onOpenMOAStrategyDialog, onOpenPlanetCharacteristicsDialog, onOpenAstroGraphDialog, onRunAstroGraphPatterns } = props;
  console.log('[TabPanel] props:', props);
  const {
    onOpenControlsDialog,
    onOpenPlanetFollowStudyDialog,
    onOpenDeclinationStudyDialog,
    onOpenPlanetaryLinesDialog,
    onOpenVisualUniverseDialog,
    onOpenAreaHighlightDialog,
    onOpenPlanetGroupDialog,
    onOpenStrategyReportStudyDialog,
    onOpenStrategyReportStudyBDialog,
    chartType,
    onToggleChartType,
    onOpenCyclesDialog,
    onTriggerAreaZoom,
  } = props;
  const handleIconClick = (action) => {
    if (action === 'open_horizontal_line_dialog') {
      if (typeof props.onOpenHorizontalLineDialog === 'function') {
        props.onOpenHorizontalLineDialog();
      }
      return;
    }
    if (action === 'clear_all_overlays') {
      clearOverlays();
      if (props.studyMode && typeof props.onExitStudyMode === 'function') {
        props.onExitStudyMode();
      }
      return;
    }
    switch (action) {
      case 'toggle_rsi_overlay':
        if (typeof onToggleRSI === 'function') onToggleRSI();
        break;
      case 'toggle_macd_overlay':
        if (typeof onToggleMACD === 'function') onToggleMACD();
        break;
      case 'open_controls':
      case 'draw_line':
        if (onOpenControlsDialog) onOpenControlsDialog();
        break;
      case 'open_planet_follow_study':
        if (onOpenPlanetFollowStudyDialog) onOpenPlanetFollowStudyDialog();
        break;
      case 'open_special_studies':
        if (props.onOpenAspectsAreaHighlightDialog) props.onOpenAspectsAreaHighlightDialog();
        break;
      case 'open_declination_study':
        if (onOpenDeclinationStudyDialog) onOpenDeclinationStudyDialog();
        break;
      case 'open_planetary_lines_study':
        if (onOpenPlanetaryLinesDialog) onOpenPlanetaryLinesDialog();
        break;
      case 'open_visual_universe':
        if (onOpenVisualUniverseDialog) onOpenVisualUniverseDialog();
        break;
      case 'open_intraday_animation':
        if (props.onOpenIntradayAnimationDialog) props.onOpenIntradayAnimationDialog();
        break;
      case 'open_chart_comparison':
        if (props.onOpenChartComparisonDialog) props.onOpenChartComparisonDialog();
        break;
      case 'open_area_highlight_study':
        if (onOpenAreaHighlightDialog) onOpenAreaHighlightDialog();
        break;
      case 'open_planet_group_study':
        if (onOpenPlanetGroupDialog) onOpenPlanetGroupDialog();
        break;
      case 'open_strategy_report_study':
        if (onOpenStrategyReportStudyDialog) onOpenStrategyReportStudyDialog();
        break;
      case 'open_strategy_report_study_b':
        if (onOpenStrategyReportStudyBDialog) onOpenStrategyReportStudyBDialog();
        break;
      case 'open_aggregate_area_highlight_study':
        if (props.onOpenAggregateAreaHighlightDialog) props.onOpenAggregateAreaHighlightDialog();
        break;
      // New: open StrategyDesignDialog for 12th icon (index 11, AdjustIcon)
      case 'open_strategy_design_dialog':
        if (onOpenStrategyDesignDialog) onOpenStrategyDesignDialog();
        break;
      case 'open_strategy_2_design_dialog':
        if (onOpenStrategy2DesignDialog) onOpenStrategy2DesignDialog();
        break;
      case 'open_time_and_event_strategy_dialog':
        if (onOpenTimeAndEventStrategyDialog) onOpenTimeAndEventStrategyDialog();
        break;
      case 'open_moa_strategy_dialog':
        if (onOpenMOAStrategyDialog) onOpenMOAStrategyDialog();
        else setMoaStrategyDialogOpen(true);
        break;
      case 'open_planet_characteristics_dialog':
        if (onOpenPlanetCharacteristicsDialog) onOpenPlanetCharacteristicsDialog();
        break;
      case 'open_astrograph_dialog':
        if (onOpenAstroGraphDialog) onOpenAstroGraphDialog();
        break;
      case 'toggle_recent_fib_projections':
        if (typeof props.onToggleRecentFibProjections === 'function') props.onToggleRecentFibProjections();
        break;
      case 'run_pivot_addition_series':
        if (typeof props.onRunPivotAdditionSeries === 'function') props.onRunPivotAdditionSeries();
        break;
      case 'run_fib_pivot_pairs':
        if (typeof props.onRunFibPivotPairs === 'function') props.onRunFibPivotPairs();
        break;
      case 'run_astrograph_patterns':
        if (onRunAstroGraphPatterns) onRunAstroGraphPatterns();
        break;
      case 'open_pattern_config_dialog':
        console.log('[TabPanel] Zodiac Wheels icon clicked (open_pattern_config_dialog)');
        if (onOpenPatternConfigDialog) onOpenPatternConfigDialog();
        break;
      case 'vertical_lines_action':
        if (props.onVerticalLinesAction) props.onVerticalLinesAction();
        break;
      // Its own case, deliberately placed BEFORE the drawing-tool chain rather than inside it.
      // This opens a dialog; it does not arm a drawing mode. Sitting among the fall-through
      // labels, its body and break captured every tool listed above it.
      case 'open_fixed_cycle_dialog':
        if (typeof props.onOpenFixedCycleDialog === 'function') props.onOpenFixedCycleDialog();
        break;
      case 'draw_vertical_line':
      case 'draw_horizontal_line':
      case 'draw_text':
      case 'draw_vertical_full':
      case 'draw_circle_empty':
      case 'draw_circle_filled':
      case 'draw_trendline':
      case 'draw_rectangle':
      case 'draw_fib_time':
      case 'draw_fibonacci':
      case 'draw_extended_trendline':
      case 'draw_pitchfork':
        if (typeof props.onSetDrawingMode === 'function') {
          // Toggle off if already active — except draw_text, where a repeat click advances the
          // size cycle instead. See drawingModeAfterClick.
          props.onSetDrawingMode(drawingModeAfterClick(props.drawingMode, action));
        }
        break;
      default:
        break;
    }
  };



  const [planetNumberDialogOpen, setPlanetNumberDialogOpen] = React.useState(false);
  const [moaStrategyDialogOpen, setMoaStrategyDialogOpen] = React.useState(false);
  const [barFloaterDialogOpen, setBarFloaterDialogOpen] = React.useState(false);


  return (
    <>
      <Box className="toolbar-gradient-bg" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', p: 0.5, overflowX: 'auto' }}>
        {ICONS.map((icon, idx) => {
          if (icon.isFlexSpacer) return <Box key={idx} sx={{ flex: 1 }} />;
          if (icon.adminOnly && !props.isAdmin) return null;
          if (icon.isTextTool) {
            // The badge advertises what the NEXT click gives — the tool cycles Small, Medium,
            // Large on each activation — so someone can pick a size before committing to a click.
            const size = props.nextTextSize || { short: 'S', label: 'Small', px: 20 };
            const isActive = props.drawingMode === 'draw_text';
            return (
              <Tooltip
                arrow
                key={idx}
                placement="bottom"
                title={`${textToolTooltip(size)} — click to place, double-click to edit, drag to move. Clicking the tool again cycles the size. Esc to cancel.`}
                disableHoverListener={!displayTooltips}
              >
                <IconButton
                  color="primary"
                  size="large"
                  onClick={() => handleIconClick('draw_text')}
                  sx={{
                    p: 0.5, mx: 0.25, flexShrink: 0,
                    ...(isActive ? { bgcolor: '#f3e5f5', border: '2px solid #7b1fa2', borderRadius: 1 } : {}),
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    {/* The glyph itself scales with the queued size, so the choice is legible at a
                        glance without reading the badge. */}
                    <text
                      x="11" y="21" textAnchor="middle" fontWeight="bold"
                      fill="#7b1fa2" fontFamily="sans-serif"
                      fontSize={size.short === 'S' ? 14 : size.short === 'M' ? 18 : 22}
                    >T</text>
                    <text
                      x="23" y="24" textAnchor="middle" fontSize="10" fontWeight="bold"
                      fill="#4a148c" fontFamily="sans-serif"
                    >{size.short}</text>
                  </svg>
                </IconButton>
              </Tooltip>
            );
          }
          if (icon.isDrawingColorPicker) {
            return (
              <Tooltip
                arrow
                key={idx}
                placement="bottom"
                title={`Colour for new drawings and text (${props.activeDrawingColor}). Existing drawings keep their own colour.`}
                disableHoverListener={!displayTooltips}
              >
                <Box
                  component="label"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 34, height: 34, mx: 0.5, borderRadius: '50%', cursor: 'pointer',
                    // A ring rather than a plain square, so it reads as a colour control and not
                    // as another icon button that happens to be tinted.
                    border: '3px solid #ffffff',
                    outline: '2px solid #90a4ae',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    boxSizing: 'border-box', flexShrink: 0,
                    // Falls back to the default rather than the bare prop. bgcolor: undefined
                    // renders a transparent circle, which looks exactly like the control being
                    // absent — the failure mode is indistinguishable from "not there", so it must
                    // not depend on a prop arriving.
                    bgcolor: props.activeDrawingColor || DEFAULT_DRAWING_COLOR,
                  }}
                >
                  {/* A native colour input: it gives the OS picker and full-spectrum choice for
                      free, and stays keyboard accessible, which a grid of swatches would not. */}
                  <input
                    type="color"
                    aria-label="Drawing colour"
                    value={props.activeDrawingColor || DEFAULT_DRAWING_COLOR}
                    onChange={e => {
                      if (typeof props.onDrawingColorChange === 'function') {
                        props.onDrawingColorChange(e.target.value);
                      }
                    }}
                    style={{ opacity: 0, width: 0, height: 0, border: 0, padding: 0 }}
                  />
                </Box>
              </Tooltip>
            );
          }
          const isActiveDrawTool = icon.action && icon.action.startsWith('draw_') && props.drawingMode === icon.action;
          return (
          <Tooltip arrow key={idx} title={icon.tooltip} placement="bottom" disableHoverListener={!displayTooltips}>
            <IconButton
              color="primary"
              size="large"
              onClick={icon.action ? () => {
                if (icon.action === 'open_horizontal_line_dialog') {
                  console.log('[DEBUG] 3-line toolbar icon clicked');
                }
                handleIconClick(icon.action);
              } : undefined}
              sx={{
                p: 0.5, mx: 0.25,
                ...(isActiveDrawTool ? { bgcolor: '#e3f2fd', border: '2px solid #1976d2', borderRadius: 1 } : {}),
              }}
            >
              {icon.isImage ? <img src={icon.component} alt={icon.alt} style={{ width: icon.size || 36, height: icon.size || 36 }} /> : icon.component}
            </IconButton>
          </Tooltip>
          );
        })}
        {/* MinimalMultiTabDialogTrigger inserted as second from last icon */}
        <Tooltip arrow title="View catalog of astro happenings for the upcoming week, month or year. Also add simple text notes to the chart if you are taking a screenshot" placement="bottom" disableHoverListener={!displayTooltips}>
  <span>
    <MinimalMultiTabDialogTrigger />
  </span>
</Tooltip>
        {/*
          Elliott Wave Analysis.

          A dedicated button rather than an entry in the ICONS array, because it is the only
          toolbar control that runs work and has to show that it is running. The array renders
          every icon through one generic IconButton with no per-icon state, so a spinner there
          would mean threading a loading flag into a loop that has no concept of one.

          The icon is a five-wave impulse: three up-legs with two retracements, which is the shape
          the engine is looking for and reads as Elliott rather than as a generic squiggle.
        */}
        <Tooltip
          arrow
          placement="bottom"
          title={props.elliottLoading
            ? 'Analysing wave structure…'
            : 'Elliott Wave Analysis — label the current chart with wave counts, targets and invalidation levels'}
          disableHoverListener={!displayTooltips}
        >
          <span>
            <IconButton
              size="small"
              onClick={() => { if (typeof props.onRunElliottAnalysis === 'function') props.onRunElliottAnalysis(); }}
              disabled={!!props.elliottLoading}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              {props.elliottLoading ? (
                <CircularProgress size={20} thickness={5} />
              ) : (
                <svg width="26" height="26" viewBox="0 0 32 32" aria-label="Elliott Wave Analysis">
                  <polyline
                    points="2,26 7,14 10,20 17,6 21,13 30,2"
                    fill="none" stroke="#1976d2" strokeWidth="2.4"
                    strokeLinejoin="round" strokeLinecap="round"
                  />
                  <circle cx="7" cy="14" r="1.7" fill="#1976d2" />
                  <circle cx="17" cy="6" r="1.7" fill="#1976d2" />
                  <circle cx="30" cy="2" r="1.7" fill="#1976d2" />
                </svg>
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/*
          Chart view cycle — last icon.

          The icon shows the view a click LEADS TO, not the one in effect. That is how the
          two-state toggle already read (line view showed the candlestick icon), so the admin-only
          third state extends the rule rather than adding a second one:

            in line        -> candlestick icon   -> click gives candlesticks
            in candlestick -> eye-off icon       -> click hides the price   (admin only)
            in hidden      -> line icon          -> click restores the line (admin only)

          For a standard user nextChartView never returns 'hidden', so the eye-off icon never
          appears and the button keeps its original two-state behaviour. The cycle enforces that
          itself — this is presentation, not the guard.
        */}
        <Tooltip arrow title={chartViewActionLabel(chartType, props.isAdmin)} placement="bottom" disableHoverListener={!displayTooltips}>
          <IconButton
            size="small"
            onClick={() => {
              onToggleChartType();
            }}
            sx={{ p: 0.5, mx: 0.25 }}
          >
            {(() => {
              switch (nextChartView(chartType, props.isAdmin)) {
                case CHART_VIEW.CANDLESTICK:
                  return <BarChartIcon style={{ color: '#1976d2' }} fontSize="small" />;
                case CHART_VIEW.HIDDEN:
                  return <VisibilityOffIcon style={{ color: '#1976d2' }} fontSize="small" />;
                default:
                  return <ShowChartIcon style={{ color: '#1976d2' }} fontSize="small" />;
              }
            })()}
          </IconButton>
        </Tooltip>
        {/* Spacer for separation between active and disabled icons */}
        <Box sx={{ width: 44, minWidth: 0 }} />
        <Tooltip arrow title="Area Zoom" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            onClick={onTriggerAreaZoom}
            sx={{ p: 0.5, mx: 0.25 }}
          >
            <img src="/images/zoom-png-2.png" alt="Area Zoom" style={{ width: 28, height: 28 }} />
          </IconButton>
        </Tooltip>
        {/*
          TrendPulse Pattern Prediction — administrators only.

          Two independent conditions, and they are not interchangeable:

            isAdmin        entitlement. Non-admins do not see the control at all, matching the
                           watermark toggle below rather than showing a permanently dead button
                           that invites clicking.
            hasAstroGraph  prerequisite. handleRunAstroGraphPatterns reads the astroGraphLine
                           overlay and, without it, alerts "No TrendPulse overlay found" and
                           returns. Enabling the button for an admin who has not run TrendPulse
                           would promise an action that cannot happen, so an admin still sees it
                           disabled until the overlay exists — with a tooltip saying why.
        */}
        {props.isAdmin && (
          <Tooltip
            arrow
            placement="bottom"
            title={hasAstroGraph
              ? 'TrendPulse Pattern Prediction — detect shapes and predict price movement (admin)'
              : 'TrendPulse Pattern Prediction (admin) — run TrendPulse first to have something to analyse'}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => handleIconClick('run_astrograph_patterns')}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              <TrendingUpIcon />
            </IconButton>
          </Tooltip>
        )}
        {props.isAdmin && (
          <Tooltip arrow title={
            !props.watermarkOverride ? 'Hide watermark' :
            props.watermarkOverride.hidden ? 'Show small watermark' :
            'Restore default watermark'
          } placement="bottom">
            <IconButton
              color="primary"
              size="large"
              onClick={() => { if (typeof props.onToggleWatermark === 'function') props.onToggleWatermark(); }}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              {!props.watermarkOverride ? <VisibilityIcon /> :
               props.watermarkOverride.hidden ? <VisibilityOffIcon /> :
               <TextDecreaseIcon />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          arrow
          placement="bottom"
          title={
            drawingCount > 0
              ? `Delete Last (X₁) — removes the most recent drawing or study${nextUndoSize > 1 ? ` (${nextUndoSize} overlays)` : ''}. ${drawingCount} on the chart. Shortcut: Ctrl+Z or Delete.`
              : 'Delete Last (X₁) — nothing to undo. Shortcut: Ctrl+Z or Delete.'
          }
        >
          {/* Wrapped: a disabled MUI button fires no events, so the Tooltip would never show and
              there would be nothing explaining why the icon is greyed out. */}
          <span>
            <IconButton
              color="primary"
              size="large"
              disabled={drawingCount === 0}
              onClick={() => { if (typeof props.onDeleteLastDrawing === 'function') props.onDeleteLastDrawing(); }}
              sx={{ p: 0.5, mx: 0.25, ...(drawingCount === 0 ? { opacity: 0.35 } : {}) }}
            >
              <svg width="28" height="28" viewBox="0 0 28 28">
                <line x1="6" y1="7" x2="19" y2="20" stroke="#d32f2f" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="19" y1="7" x2="6" y2="20" stroke="#d32f2f" strokeWidth="2.5" strokeLinecap="round" />
                {/* The subscript 1 is what distinguishes this from Clear All at a glance. */}
                <text x="23" y="24" textAnchor="middle" fontSize="12" fontWeight="bold"
                      fill="#b71c1c" fontFamily="sans-serif">1</text>
              </svg>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip arrow title="Remove all astro studies from the chart and get the chart ready to add different studies. Does not remove all technical studies" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            onClick={() => handleIconClick('clear_all_overlays')}
            sx={{ p: 0.5, mx: 0.25 }}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ width: 44, minWidth: 0 }} />
        {/* Group of inactive icons on the right */}
        <Tooltip arrow title="Geometric Patterns (Not Implemented)" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <ArchitectureIcon />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Historical Ephemeris (Not Implemented)" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <HistoryEduIcon />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Custom Formulas (Not Implemented)" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <FunctionsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Cycle Analysis (Not Implemented)" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <ChangeHistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Track planets" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <TrackChangesIcon />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Planet Group Study" placement="bottom">
          <IconButton
            color="primary"
            size="large"
            disabled
            sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
          >
            <PublicIcon />
          </IconButton>
        </Tooltip>
        {/* Spacer to push messages icon to far right */}
        <Box flexGrow={1} />

        {/* Bar Floater Selection Icon */}
        <Tooltip arrow title="Select what information to display when hovering over chart bars: OHLCV, Planetary Aspects, or Planet Positions" placement="bottom">
          <IconButton
            onClick={() => setBarFloaterDialogOpen(true)}
            sx={{ ml: 1 }}
            aria-label="Bar Floater Selection"
          >
            <ViewListIcon style={{ color: '#1976d2' }} />
          </IconButton>
        </Tooltip>

        {/* Cycles Dialog Icon */}
        <Tooltip arrow title="Zoom the chart and click the two bars that you think define a cycle. Open the dialog and select type parameters (color, fib ratios if desired and line thickness) and click &quot;Display&quot; to see the chart updated" placement="bottom">
          <IconButton
            onClick={onOpenCyclesDialog}
            sx={{ ml: 1 }}
            aria-label="Open Cycles Dialog"
          >
            <img src="/images/vertical-lines.png" alt="Cycles Dialog" style={{ width: 36, height: 36 }} />
          </IconButton>
        </Tooltip>

        {/* Messages Panel Icon */}
        <Tooltip arrow title="Area Highlight and Strategy Studies will automatically populate and display this dialog" placement="bottom">
          <IconButton
            color={props.messagesOpen ? 'secondary' : 'default'}
            onClick={() => props.setMessagesOpen(true)}
            sx={{ ml: 2 }}
            aria-label="Show Messages Panel"
          >
            <MessageIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Planet Number Popover Dialog */}
      <Popover
        open={planetNumberDialogOpen}
        onClose={() => setPlanetNumberDialogOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, display: 'flex', flexDirection: 'row', borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper', border: '1px solid #aaa', minWidth: 0 } }}
      >
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <Box key={n} sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: '#f5f5f5', m: 0.5, fontWeight: 'bold', fontSize: 18, cursor: 'pointer', border: '1px solid #bbb', '&:hover': { bgcolor: '#e0e0e0' } }}>{n}</Box>
        ))}
      </Popover>

      <MessagesPanelDialog
        open={props.messagesOpen}
        onClose={() => props.setMessagesOpen(false)}
        tabContents={props.messagesTabContents}
        studyTitle={props.studyTitle}
        journalMarkdown={props.journalMarkdown}
        onJournalChange={props.onJournalChange}
        onJournalReset={props.onJournalReset}
        journalEditing={props.journalEditing}
        onJournalEditingChange={props.onJournalEditingChange}
        tabIndex={props.messagesTabIndex}
        onTabIndexChange={props.onMessagesTabIndexChange}
      />
      {/* Aspects Area Highlight Dialog integration */}
      {/*
      <AspectsAreaHighlightDialog
        open={...}
        onClose={...}
        onRun={handleAspectsAreaHighlightRun}
        barData={...}
      />
      */}

    {/* Vedic Aspects Study Dialog */}
        <MOAStrategyDialog
      open={moaStrategyDialogOpen}
      onClose={() => setMoaStrategyDialogOpen(false)}
      barData={props.barData}
      extendedBarData={props.extendedBarData}
      setMessagesTabContents={props.setMessagesTabContents}
      setMessagesOpen={props.setMessagesOpen}
      setStudyTitle={props.setStudyTitle}
      onJournalAppend={props.onJournalAppend}
    />
    <BarFloaterSelectionDialog
      open={barFloaterDialogOpen}
      onClose={() => setBarFloaterDialogOpen(false)}
      currentMode={props.barFloaterMode || 'ohlcv'}
      onApply={(mode) => {
        if (typeof props.onBarFloaterModeChange === 'function') props.onBarFloaterModeChange(mode);
      }}
    />
    <VedicAspectsStudyDialog 
      open={vedicAspectsOpen}
      onClose={() => setVedicAspectsOpen(false)}
      barData={props.barData}
      extendedBarData={props.extendedBarData}
      onJournalAppend={props.onJournalAppend}
      onRun={({ stats, futureDates }) => {
            if (typeof props.setMessagesTabContents === 'function') {
              const { formatMessagesPanelTabs } = require('../utils/messagesPanelUtils');
              const tabs = formatMessagesPanelTabs(stats, futureDates);
              props.setMessagesTabContents(tabs);
            }
            if (typeof props.setMessagesOpen === 'function') props.setMessagesOpen(true);
          }}
    />
    </>
  );
};

TabPanel.propTypes = {
  onVerticalLinesAction: PropTypes.func.isRequired,
  onOpenPatternConfigDialog: PropTypes.func,
  onOpenHorizontalLineDialog: PropTypes.func.isRequired,
  showRSI: PropTypes.bool,
  onToggleRSI: PropTypes.func,
  onToggleMACD: PropTypes.func,
  barData: PropTypes.object,
  extendedBarData: PropTypes.object,
  theme: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.any,
  viewMode: PropTypes.string,
  chartColors: PropTypes.object,
  overlays: PropTypes.array,
  onOpenStrategyDesignDialog: PropTypes.func,
  onOpenStrategy2DesignDialog: PropTypes.func,
  onOpenTimeAndEventStrategyDialog: PropTypes.func,
  onOpenMOAStrategyDialog: PropTypes.func,
  onOpenPlanetCharacteristicsDialog: PropTypes.func,
  onOpenAstroGraphDialog: PropTypes.func,
  onRunAstroGraphPatterns: PropTypes.func,
  isTimeAndEventStrategyDialogOpen: PropTypes.bool,
  onCloseTimeAndEventStrategyDialog: PropTypes.func,
    onOpenAggregateAreaHighlightDialog: PropTypes.func,
  onOpenStrategyReportStudyDialog: PropTypes.func.isRequired,
  onOpenStrategyReportStudyBDialog: PropTypes.func.isRequired,
  onOpenControlsDialog: PropTypes.func.isRequired,
  onOpenPlanetFollowStudyDialog: PropTypes.func.isRequired,
  onOpenAspectsAreaHighlightDialog: PropTypes.func.isRequired,
  onOpenDeclinationStudyDialog: PropTypes.func.isRequired,
  onOpenPlanetaryLinesDialog: PropTypes.func.isRequired,
  onOpenVisualUniverseDialog: PropTypes.func.isRequired,
  onOpenIntradayAnimationDialog: PropTypes.func,
  onOpenChartComparisonDialog: PropTypes.func,
  onOpenAreaHighlightDialog: PropTypes.func.isRequired,
  onOpenPlanetGroupDialog: PropTypes.func.isRequired,
  // 'hidden' is admin-only; App.js normalises before passing, so a standard user never sees it.
  chartType: PropTypes.oneOf(['candlestick', 'line', 'hidden']).isRequired,
  // Which Messages panel tab is showing. Owned by App so a study can open the panel on the tab it
  // wrote to — a Journal-only study opening on Summary looks like it produced nothing.
  messagesTabIndex: PropTypes.number,
  onMessagesTabIndexChange: PropTypes.func,
  // Elliott Wave Analysis: the click handler, and whether a run is in flight so the button can
  // show a spinner and refuse a second click while the first is still going.
  onRunElliottAnalysis: PropTypes.func,
  elliottLoading: PropTypes.bool,
  onToggleChartType: PropTypes.func.isRequired,
  messagesTabContents: PropTypes.arrayOf(PropTypes.node).isRequired,
  setMessagesTabContents: PropTypes.func.isRequired,
  messagesOpen: PropTypes.bool.isRequired,
  setMessagesOpen: PropTypes.func.isRequired,
  studyTitle: PropTypes.string,
  setStudyTitle: PropTypes.func,
  journalMarkdown: PropTypes.string,
  onJournalChange: PropTypes.func,
  onJournalReset: PropTypes.func,
  onJournalAppend: PropTypes.func,
  onOpenCyclesDialog: PropTypes.func,
  onTriggerAreaZoom: PropTypes.func,
  drawingMode: PropTypes.string,
  nextTextSize: PropTypes.shape({ short: PropTypes.string, label: PropTypes.string, px: PropTypes.number }),
  journalEditing: PropTypes.bool,
  onJournalEditingChange: PropTypes.func,
  activeDrawingColor: PropTypes.string,
  onDrawingColorChange: PropTypes.func,
  onSetDrawingMode: PropTypes.func,
  onDeleteLastDrawing: PropTypes.func,
  onRunPivotAdditionSeries: PropTypes.func,
  onToggleRecentFibProjections: PropTypes.func,
  onOpenFixedCycleDialog: PropTypes.func,
  onRunFibPivotPairs: PropTypes.func,
  studyMode: PropTypes.bool,
  onExitStudyMode: PropTypes.func,
  barFloaterMode: PropTypes.string,
  onBarFloaterModeChange: PropTypes.func,
  isAdmin: PropTypes.bool,
  watermarkOverride: PropTypes.shape({
    hidden: PropTypes.bool,
    text: PropTypes.string,
    fontSize: PropTypes.number,
  }),
  onToggleWatermark: PropTypes.func,
};

export default TabPanel;
